/**
 * Scrapbook stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const scrapbookStickersPack: ToilePack = {
    slug: 'scrapbook-stickers',
    category: 'sticker',
    name: 'Scrapbook stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'tags', label: 'Paper tags', subject: 'A sheet of small paper tags in three shapes with blank centres'},
        {key: 'frames', label: 'Frames', subject: 'A sheet of small photo frames and corners'},
        {key: 'ephemera', label: 'Ephemera', subject: 'A sheet of small ephemera: a ticket, a stamp, a postmark, a ribbon'},
    ],
    styles: [
        {key: 'kiss', label: 'Kiss-cut sheet', style: 'kiss-cut sticker sheet, glossy stickers on a matte backing, offset white borders, cut lines drawn', palette: 'Mixed bright colours on a pale backing sheet', framing: 'Nine to twelve stickers arranged in rows on one sheet with a margin, a plain background between them'},
        {key: 'die', label: 'Die-cut set', style: 'die-cut sticker set, each shape cut to its own outline, white border', palette: 'One bright flat colour, no gradient', framing: 'The set laid out spaced apart on a plain ground'},
        {key: 'planner', label: 'Planner strip', style: 'planner sticker strip, small tidy icons in one pastel palette, cut lines drawn', palette: 'One soft pastel palette', framing: 'Small stickers in neat rows on a narrow strip with a margin, on a plain background'},
    ],
    directions: ['in a travel theme', 'for a baby book', 'in muted vintage tones'],
    directionHint: 'try: in a travel theme',
    seo: {
        title: 'Scrapbook Stickers: Paper Tags, Frames, Ephemera',
        metaDescription: 'Nine scrapbook stickers — paper tags, frames, small ephemera — with blank centres, in kiss-cut, die-cut, and planner finishes.',
        h1: 'Scrapbook stickers: paper tags, frames, and ephemera in three finishes',
        tagline: 'Nine scrapbook stickers, three page essentials, three finishes each.',
        intro: 'The sticker for the page: a set of paper tags, a set of frames, a set of small ephemera, each with blank centres so your own photo or note shows through. Rendered kiss-cut, die-cut, and planner-strip to match how scrapbookers cut and layer them. Ask for a specific theme — travel, baby, holiday — and the same three finishes carry it.',
        useCases: [
            'a memory-keeping scrapbook page',
            'a photo frame or tag accent',
            'a junk-journal ephemera layer',
            'a memory-keeping planner spread',
            'a printable scrapbook sticker sheet',
        ],
        faq: [
            {q: 'Are the frame and tag centres left empty on purpose?', a: "Yes — they're designed as frames for your own photo, note, or ticket stub, not standalone decorations."},
            {q: "What's the difference between kiss-cut, die-cut, and planner finishes?", a: 'Kiss-cut leaves the sticker on its backing for peeling, die-cut trims to its own edge, and planner-strip fits flush inside a journal box — pick to match your layering style.'},
            {q: 'Can I get a themed set, like travel or a new baby?', a: 'Yes — the direction field takes any theme, rendered in the same three finishes.'},
            {q: 'Do these work in a digital scrapbook, or only printed?', a: 'These are designed as printed stickers; a digital scrapbooker can still use the flat image files as page elements.'},
            {q: "Can I use these in a scrapbook I'm making for a client?", a: 'Personal and one-off client project use is fine; check the license terms before reselling the designs themselves.'},
        ],
    },
}
